/**
 * ScenarioAnalysisSection - Clean, Google Flights-inspired scenario visualization
 * 
 * Design principles:
 * - Minimal, clean interface with lots of white space
 * - Icons for section labels
 * - Uniform formatting across all sections
 * - Explanatory intro text
 * - Journalistic narrative style
 */

import React from 'react'
import { ScenarioAxis, Source, ScenarioState } from '../../../lib/cockpit/schemas/cig'

interface ScenarioAnalysisSectionProps {
  scenarioAxis: ScenarioAxis
  detailedScenarios?: DetailedScenario[]
  sources?: Source[]
  topicName?: string
  className?: string
}

export interface DetailedScenario {
  id: string
  name: string
  subtitle?: string
  description?: string
  probability?: number
  probabilityBand?: string
  tone: 'upside' | 'base' | 'downside' | 'mixed'
  assumptions?: { text: string; rationale?: string }[]
  triggers?: { description: string; observability?: string; confidence?: number }[]
  metricImpacts?: string[]
  sourceIds?: string[]
  countryAnalysis?: {
    country: string
    analysis: string
    metrics?: { label: string; value: string; trend?: 'up' | 'down' | 'neutral' }[]
    sourceIds?: string[]
  }[]
}

const TONE_CONFIG = {
  upside: { accent: '#059669', label: 'Upside' },
  base: { accent: '#d97706', label: 'Base Case' },
  downside: { accent: '#dc2626', label: 'Downside' },
  mixed: { accent: '#2563eb', label: 'Mixed' },
}

// Icons for section labels
const SECTION_ICONS = {
  what: (
    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  triggers: (
    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  impact: (
    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
    </svg>
  ),
  assumptions: (
    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  signal: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.348 14.651a3.75 3.75 0 010-5.303m5.304 0a3.75 3.75 0 010 5.303m-7.425 2.122a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.789m13.788 0c3.808 3.808 3.808 9.981 0 13.79M12 12h.008v.007H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  ),
}

// Signal type configurations for visual styling
const SIGNAL_TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  metric: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
    color: '#3b82f6',
    label: 'Metric',
  },
  data: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
    color: '#8b5cf6',
    label: 'Data Source',
  },
  news: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
      </svg>
    ),
    color: '#f59e0b',
    label: 'News & Reports',
  },
  primary: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    color: '#10b981',
    label: 'Primary Research',
  },
  rel: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    ),
    color: '#ec4899',
    label: 'Relationship',
  },
  market: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
      </svg>
    ),
    color: '#06b6d4',
    label: 'Market Signal',
  },
}

// Parse signal to extract clean data for display
function parseSignalData(label: string, definition: string): { 
  title: string
  type: keyof typeof SIGNAL_TYPE_CONFIG
  metric?: string
  threshold?: string
  implication?: string
  cleanDefinition: string
} {
  const text = definition || label
  
  // Detect signal type from ID patterns in the text
  let type: keyof typeof SIGNAL_TYPE_CONFIG = 'metric'
  
  // Check for ID patterns: MET_*, DATA_*, NEWS::*, PRIMARY::*, REL_*, MARKET::*
  if (/\bMET_[A-Z_]+\b/.test(text)) {
    type = 'metric'
  } else if (/\bDATA_[A-Z_]+\b|DATA::\d+/.test(text)) {
    type = 'data'
  } else if (/\bNEWS::\d+/.test(text)) {
    type = 'news'
  } else if (/\bPRIMARY::\d+/.test(text)) {
    type = 'primary'
  } else if (/\bREL_[A-Z_]+\b/.test(text)) {
    type = 'rel'
  } else if (/\bMARKET::\d+/.test(text)) {
    type = 'market'
  }
  
  // Extract a short title (first key concept before technical details)
  let title = ''
  
  // Try to extract the main subject/metric name
  // Look for patterns like "Enterprise AI usage" or "POC abandonment" etc.
  const subjectPatterns = [
    /^([A-Z][a-z]+(?:\s+[A-Za-z]+){0,4})\s+(?:reaches|tracks|stays|rises|exceeds|continues|increases|becomes)/i,
    /^([A-Z][a-z]+(?:\s+[A-Za-z]+){0,3})\s+(?:\(|from|usage|adoption|rate)/i,
    /^(EU\/UK\s+[a-z]+(?:\s+[a-z]+){0,2})/i,
    /^(At least\s+[a-z]+(?:\s+[a-z]+){0,3})/i,
    /^(Rising\s+[a-z]+(?:\s+[a-z]+){0,2})/i,
  ]
  
  for (const pattern of subjectPatterns) {
    const match = text.match(pattern)
    if (match) {
      title = match[1].trim()
      break
    }
  }
  
  // Fallback: use first 4-5 words before any numbers or parentheses
  if (!title) {
    const words = text.split(/\s+/).slice(0, 5)
    const cutoff = words.findIndex(w => /^\d|^\(|^>=|^<=|^>|^</.test(w))
    title = words.slice(0, cutoff > 0 ? cutoff : 4).join(' ')
  }
  
  // Clean up the title
  title = title
    .replace(/\s+/g, ' ')
    .replace(/[,;:]$/, '')
    .trim()
  
  // Extract threshold/target (numbers with % or comparison operators)
  let threshold = ''
  const thresholdMatch = text.match(/(>=?\s*\d+%?|<=?\s*\d+%?|\d+%\s*(?:to|by)\s*\d+)/i)
  if (thresholdMatch) {
    threshold = thresholdMatch[1]
  }
  
  // Extract implication (what it means if threshold is hit/missed)
  let implication = ''
  const implicationPatterns = [
    /(?:indicates|signals?|supports?|suggests?)\s+([^.;]+)/i,
    /(?:if it|when it)\s+(?:stays|reaches|exceeds|falls|misses)[^,]+,\s*([^.;]+)/i,
  ]
  for (const pattern of implicationPatterns) {
    const match = text.match(pattern)
    if (match) {
      implication = match[1].trim()
      break
    }
  }
  
  // Clean definition: remove raw IDs for cleaner display
  const cleanDefinition = text
    .replace(/\b(MET_[A-Z_]+|DATA_[A-Z_]+|NEWS::\d+|PRIMARY::\d+|REL_[A-Z_]+|MARKET::\d+)\b/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s*[,;:]\s*/, '')
    .trim()
  
  return { title, type, metric: threshold, threshold, implication, cleanDefinition }
}

// Key Signals Carousel Component
interface SignalTrigger {
  id?: string
  label: string
  definition?: string
}

function KeySignalsCarousel({ triggers }: { triggers: SignalTrigger[] }) {
  const [activeGroup, setActiveGroup] = React.useState<string>('all')
  const [currentPage, setCurrentPage] = React.useState(0)
  const SIGNALS_PER_PAGE = 3
  
  // Group signals by type
  const groupedSignals = React.useMemo(() => {
    const groups: Record<string, { trigger: SignalTrigger; signalData: ReturnType<typeof parseSignalData> }[]> = {
      all: [],
      metric: [],
      data: [],
      news: [],
      primary: [],
      rel: [],
      market: [],
    }
    
    triggers.forEach(trigger => {
      const signalData = parseSignalData(trigger.label, trigger.definition || '')
      const item = { trigger, signalData }
      groups.all.push(item)
      if (groups[signalData.type]) {
        groups[signalData.type].push(item)
      }
    })
    
    return groups
  }, [triggers])
  
  // Get available groups (only show tabs for groups with signals)
  const availableGroups = React.useMemo(() => {
    const groups: { key: string; label: string; count: number; color: string }[] = [
      { key: 'all', label: 'All Signals', count: groupedSignals.all.length, color: '#6b7280' },
    ]
    
    Object.entries(SIGNAL_TYPE_CONFIG).forEach(([key, config]) => {
      if (groupedSignals[key]?.length > 0) {
        groups.push({
          key,
          label: config.label,
          count: groupedSignals[key].length,
          color: config.color,
        })
      }
    })
    
    return groups
  }, [groupedSignals])
  
  // Get current signals to display
  const currentSignals = groupedSignals[activeGroup] || []
  const totalPages = Math.ceil(currentSignals.length / SIGNALS_PER_PAGE)
  const displayedSignals = currentSignals.slice(
    currentPage * SIGNALS_PER_PAGE,
    (currentPage + 1) * SIGNALS_PER_PAGE
  )
  
  // Reset page when group changes
  React.useEffect(() => {
    setCurrentPage(0)
  }, [activeGroup])
  
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)))
  }
  
  return (
    <div className="border-t border-gray-100 dark:border-gray-800 pt-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
          {SECTION_ICONS.signal}
        </div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
          Key Signals to Monitor
        </h4>
        <span className="ml-auto text-xs text-gray-400">
          {currentSignals.length} signal{currentSignals.length !== 1 ? 's' : ''}
        </span>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 pl-9">
        Observable indicators that may signal a shift between scenarios.
      </p>
      
      {/* Category Tabs - Uniform pill style with black active state */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1 pl-9">
        {availableGroups.map(group => (
          <button
            key={group.key}
            onClick={() => setActiveGroup(group.key)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
              transition-all whitespace-nowrap
              ${activeGroup === group.key
                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }
            `}
          >
            {group.label}
            <span 
              className={`
                px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                ${activeGroup === group.key
                  ? 'bg-white/20 dark:bg-gray-900/20 text-white dark:text-gray-900'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }
              `}
            >
              {group.count}
            </span>
          </button>
        ))}
      </div>
      
      {/* Signal Cards */}
      <div className="space-y-3">
        {displayedSignals.map(({ trigger, signalData }, idx) => {
          const config = SIGNAL_TYPE_CONFIG[signalData.type] || SIGNAL_TYPE_CONFIG.metric
          
          return (
            <div 
              key={trigger.id || idx}
              className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="flex items-stretch">
                {/* Left color bar */}
                <div 
                  className="w-1 flex-shrink-0"
                  style={{ backgroundColor: config.color }}
                />
                
                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon and type */}
                    <div className="flex-shrink-0">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${config.color}10`, color: config.color }}
                      >
                        {config.icon}
                      </div>
                    </div>
                    
                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      {/* Type badge + Title */}
                      <div className="flex items-center gap-2 mb-1.5">
                        <span 
                          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: `${config.color}15`, color: config.color }}
                        >
                          {config.label}
                        </span>
                      </div>
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug mb-2">
                        {signalData.title}
                      </h5>
                      
                      {/* Clean description */}
                      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                        {signalData.cleanDefinition}
                      </p>
                      
                      {/* Threshold and implication badges */}
                      {(signalData.threshold || signalData.implication) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {signalData.threshold && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                              Target: {signalData.threshold}
                            </span>
                          )}
                          {signalData.implication && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {signalData.implication.length > 35 
                                ? signalData.implication.substring(0, 35) + '...' 
                                : signalData.implication}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                className={`
                  w-2 h-2 rounded-full transition-all
                  ${i === currentPage 
                    ? 'bg-gray-800 dark:bg-white w-4' 
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }
                `}
              />
            ))}
          </div>
          
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <span className="text-xs text-gray-400 ml-2">
            {currentPage + 1} / {totalPages}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Clean up topic name for display in the Origin box.
 * Removes "Current state of" prefixes and formats properly.
 */
function cleanTopicForOrigin(rawTopic: string): string {
  if (!rawTopic || rawTopic === 'this topic' || rawTopic === 'this analysis') {
    return 'Current Trajectory Analysis'
  }
  
  let cleaned = rawTopic.trim()
  
  // Remove "Current state of" if already present (don't duplicate)
  cleaned = cleaned.replace(/^current\s+state\s+of\s+/i, '').trim()
  
  // Remove "Once" prefix from questions
  cleaned = cleaned.replace(/^once\s+/i, '').trim()
  
  // Remove trailing question marks
  cleaned = cleaned.replace(/\?+$/, '').trim()
  
  // If it looks like a full question, try to extract the key topic
  if (/^(how|what|why|when|where|will|can|should)/i.test(cleaned)) {
    // Extract topic from question patterns
    const questionPatterns = [
      // "How has X extended its power..." -> "X Regional Influence"
      /how\s+(?:has|have|is|will)\s+(.+?)\s+(?:extended|grown|expanded|projected|influenced)/i,
      // "What is the future of X" -> "X Outlook"
      /what\s+(?:is|are)\s+(?:the\s+)?(?:future|trajectory|outlook)\s+(?:of|for)\s+(.+?)(?:\?|$)/i,
      // "How is the X industry going to shape" -> "X Industry Evolution"
      /how\s+(?:is|will)\s+(?:the\s+)?(.+?)\s+(?:industry|market|sector)?\s*(?:going\s+to\s+)?(?:shape|evolve|change)/i,
      // Generic: get first meaningful noun phrase
      /(?:how|what|why|will|can)\s+(?:is|are|has|have|does|do)?\s*(?:the\s+)?(.+?)(?:\s+(?:going|change|evolve|develop|work|affect)|[?.]|$)/i,
    ]
    
    for (const pattern of questionPatterns) {
      const match = cleaned.match(pattern)
      if (match && match[1]) {
        const extracted = match[1].trim()
        if (extracted.length > 3 && extracted.length < 60) {
          cleaned = extracted
          break
        }
      }
    }
  }
  
  // Remove common suffixes that don't add value for origin box
  cleaned = cleaned.replace(/\s+trajectory$/i, '').trim()
  cleaned = cleaned.replace(/\s+analysis$/i, '').trim()
  cleaned = cleaned.replace(/\s+outlook$/i, '').trim()
  
  // Capitalize first letter of each word for proper noun treatment
  // But be careful with acronyms
  const words = cleaned.split(' ')
  const formatted = words.map((word, idx) => {
    // Keep ALL CAPS words as-is (likely acronyms)
    if (word === word.toUpperCase() && word.length <= 5) {
      return word
    }
    // Capitalize first letter, keep rest of casing
    return word.charAt(0).toUpperCase() + word.slice(1)
  }).join(' ')
  
  // If still too long, truncate smartly
  if (formatted.length > 80) {
    const truncated = formatted.slice(0, 77)
    const lastSpace = truncated.lastIndexOf(' ')
    return lastSpace > 40 ? truncated.slice(0, lastSpace) + '...' : truncated + '...'
  }
  
  return formatted
}

function parseProbability(prob: number | string | undefined): { display: string; percentage: number } {
  if (prob === undefined || prob === null) return { display: '--', percentage: 0 }
  
  if (typeof prob === 'number') {
    const pct = prob <= 1 ? Math.round(prob * 100) : Math.round(prob)
    return { display: `${pct}%`, percentage: pct }
  }
  
  const str = String(prob).toUpperCase()
  if (str === 'LOW') return { display: '20%', percentage: 20 }
  if (str === 'MEDIUM' || str === 'BASE') return { display: '45%', percentage: 45 }
  if (str === 'HIGH') return { display: '65%', percentage: 65 }
  
  const rangeMatch = str.match(/(\d+)\s*-\s*(\d+)/)
  if (rangeMatch) {
    const avg = Math.round((parseInt(rangeMatch[1]) + parseInt(rangeMatch[2])) / 2)
    return { display: `${avg}%`, percentage: avg }
  }
  
  const numMatch = str.match(/(\d+)/)
  if (numMatch) return { display: `${numMatch[1]}%`, percentage: parseInt(numMatch[1]) }
  
  // No fallback to 33% - return 0 to indicate "needs calculation"
  return { display: str || '--', percentage: 0 }
}

function determineTone(name: string, index: number, total: number): 'upside' | 'base' | 'downside' | 'mixed' {
  const lower = (name || '').toLowerCase()
  
  // Priority 1: Explicit type prefix at start of name
  if (/^(base\s*case|baseline|status\s*quo)\b/.test(lower)) return 'base'
  if (/^(upside|bull\s*case|best\s*case|optimistic)\b/.test(lower)) return 'upside'
  if (/^(downside|bear\s*case|worst\s*case|pessimistic)\b/.test(lower)) return 'downside'
  if (/^(mixed|moderate|neutral)\b/.test(lower)) return 'mixed'

  // Priority 2: Keyword matching on name
  if (lower.includes('bull') || lower.includes('upside') || lower.includes('optimistic') || 
      lower.includes('acceleration') || lower.includes('best') || lower.includes('governed') ||
      lower.includes('widening') || lower.includes('gap')) return 'upside'
  if (lower.includes('bear') || lower.includes('downside') || lower.includes('pessimistic') || 
      lower.includes('backlash') || lower.includes('retrench') || lower.includes('worst') ||
      lower.includes('reversal') || lower.includes('decline') || lower.includes('blocked') ||
      lower.includes('litigated') || lower.includes('risk') || lower.includes('adverse') ||
      lower.includes('erode') || lower.includes('harden') || lower.includes('collapse')) return 'downside'
  if (lower.includes('mixed') || lower.includes('patchwork') || lower.includes('uneven') ||
      lower.includes('hybrid') || lower.includes('equilibrium') || lower.includes('moderate') || 
      lower.includes('balanced') || lower.includes('fragmented')) return 'mixed'
  if (lower.includes('base') || lower.includes('status quo') || lower.includes('continuation') ||
      lower.includes('current')) return 'base'
  
  // Priority 3: Position-based fallback
  if (total === 1) return 'base'
  if (index === 0) return 'upside'
  if (index === total - 1) return 'downside'
  return 'base'
}

/**
 * Detect and fix uniform probability distributions (e.g., 33%/33%/33% or 33%/33%/34%)
 * This is a common LLM failure mode where it doesn't properly differentiate scenarios.
 * We recalculate based on scenario types.
 */
function fixUniformProbabilities(scenarios: DetailedScenario[]): DetailedScenario[] {
  if (scenarios.length < 2) return scenarios
  
  // Check if all probabilities are essentially uniform
  const probs = scenarios.map(s => s.probability || 0)
  const sumTo100 = Math.abs(probs.reduce((a, b) => a + b, 0) - 100) < 5
  
  // Check for near-uniform distribution (all values within 5% of each other)
  // This catches 33/33/34, 25/25/25/25, etc.
  const minProb = Math.min(...probs.filter(p => p > 0))
  const maxProb = Math.max(...probs)
  const allNearUniform = maxProb - minProb <= 5 && probs.every(p => p > 0)
  
  // Also check if all values are exactly the same or all zero
  const uniqueProbs = new Set(probs)
  const allSame = uniqueProbs.size === 1 || (uniqueProbs.size === 2 && uniqueProbs.has(0))
  
  // Check if probabilities follow expected pattern (base > upside/downside)
  // Good: 45/30/25, Bad: 33/33/34
  const hasGoodDifferentiation = (() => {
    if (!sumTo100 || probs.length < 3) return false
    const sorted = [...probs].sort((a, b) => b - a)
    // The highest probability should be at least 8% higher than the lowest
    return sorted[0] - sorted[sorted.length - 1] >= 8
  })()
  
  // If probabilities are properly differentiated, keep them
  if (hasGoodDifferentiation) {
    return scenarios
  }
  
  // Need to recalculate if: all same, all near-uniform, or doesn't sum to 100
  const needsRecalculation = allSame || allNearUniform || !sumTo100
  if (!needsRecalculation) {
    return scenarios
  }
  
  // Need to recalculate - use tone-based heuristics
  console.log('[ScenarioAnalysis] Detected uniform probabilities, recalculating based on scenario types')
  
  // Count scenarios by type
  const byTone: Record<string, DetailedScenario[]> = {
    upside: [],
    base: [],
    mixed: [],
    downside: [],
  }
  
  for (const scenario of scenarios) {
    byTone[scenario.tone].push(scenario)
  }
  
  // Assign base probabilities by type
  // Base case gets most weight, followed by mixed, then upside/downside
  const baseProbs: Record<string, number> = {
    base: 42,
    mixed: 25,
    upside: 28,
    downside: 25,
  }
  
  // If multiple scenarios of same type, split the probability
  const assignedProbs: Map<string, number> = new Map()
  let totalAssigned = 0
  
  for (const [tone, toneScenarios] of Object.entries(byTone)) {
    if (toneScenarios.length === 0) continue
    
    const probPerScenario = Math.round(baseProbs[tone] / toneScenarios.length)
    for (const scenario of toneScenarios) {
      assignedProbs.set(scenario.id, probPerScenario)
      totalAssigned += probPerScenario
    }
  }
  
  // Normalize to 100%
  const scaleFactor = 100 / totalAssigned
  
  // Find the largest scenario to absorb rounding errors
  let maxScenarioId = scenarios[0]?.id
  let largestScenarioProb = 0
  
  const result: DetailedScenario[] = []
  let runningTotal = 0
  
  for (let i = 0; i < scenarios.length; i++) {
    const scenario = scenarios[i]
    const scenarioBaseProb = assignedProbs.get(scenario.id) || 33
    let scaledProb = Math.round(scenarioBaseProb * scaleFactor)
    
    // Track max for rounding adjustment
    if (scaledProb > largestScenarioProb) {
      largestScenarioProb = scaledProb
      maxScenarioId = scenario.id
    }
    
    // For last scenario, ensure we hit exactly 100
    if (i === scenarios.length - 1) {
      scaledProb = 100 - runningTotal
    } else {
      runningTotal += scaledProb
    }
    
    result.push({
      ...scenario,
      probability: Math.max(5, Math.min(65, scaledProb)), // Clamp between 5-65%
      probabilityBand: `${scaledProb}%`,
    })
  }
  
  // Final normalization pass to ensure sum is exactly 100
  const finalSum = result.reduce((sum, s) => sum + (s.probability || 0), 0)
  if (finalSum !== 100) {
    // Adjust the largest scenario
    const largestScenario = result.find(s => s.id === maxScenarioId)
    if (largestScenario) {
      const diff = 100 - finalSum
      largestScenario.probability = (largestScenario.probability || 0) + diff
      largestScenario.probabilityBand = `${largestScenario.probability}%`
    }
  }
  
  return result
}

/**
 * Check if a trigger is generic/placeholder that should be replaced
 */
function isGenericTrigger(trigger: string): boolean {
  if (!trigger) return true
  
  const lower = trigger.toLowerCase().trim()
  
  // Exact matches
  const genericExact = [
    'core assumptions proving accurate over time',
    'key stakeholder actions aligning with expectations',
    'core assumptions proving accurate over time; key stakeholder actions aligning with expectations',
    'market conditions change',
    'major market shift',
    'no major disruptions',
    'conditions change',
    'monitor leading indicators',
    'trigger event',
  ]
  if (genericExact.some(g => lower.includes(g))) {
    return true
  }
  
  return false
}

/**
 * Generate contextual trigger based on scenario type
 */
function generateContextualTrigger(scenarioName: string, tone: 'upside' | 'base' | 'downside' | 'mixed'): string {
  const name = scenarioName.toLowerCase()
  
  if (tone === 'upside') {
    if (name.includes('streaming') || name.includes('media')) {
      return 'Subscriber growth exceeds 15% YoY; content engagement metrics improve significantly'
    }
    if (name.includes('tech') || name.includes('platform')) {
      return 'Platform adoption accelerates; key partnerships or acquisitions announced'
    }
    if (name.includes('growth') || name.includes('expansion')) {
      return 'Revenue growth exceeds projections; market share gains sustained over 2+ quarters'
    }
    return 'Key growth metrics exceed baseline projections for 2+ consecutive quarters'
  }
  
  if (tone === 'downside') {
    if (name.includes('block') || name.includes('litigat')) {
      return 'Regulatory rejection or extended litigation timeline; deal conditions unmet'
    }
    if (name.includes('backlash') || name.includes('decline')) {
      return 'Consumer sentiment turns negative; key metrics decline 15%+ from baseline'
    }
    if (name.includes('fragment') || name.includes('shock')) {
      return 'Market fragmentation accelerates; competitive dynamics shift unfavorably'
    }
    return 'Risk indicators breach warning thresholds; key assumptions prove invalid'
  }
  
  if (tone === 'mixed' || tone === 'base') {
    if (name.includes('hybrid') || name.includes('equilibrium')) {
      return 'Market settles into stable pattern; neither strong growth nor decline materializes'
    }
    if (name.includes('patchwork') || name.includes('moderate')) {
      return 'Mixed signals persist; different market segments show divergent trajectories'
    }
    if (name.includes('regulated') || name.includes('constrained')) {
      return 'Regulatory framework established with moderate restrictions; compliance costs manageable'
    }
    return 'Core market dynamics remain stable; no significant deviation from current trends'
  }
  
  return 'Observable shift in key indicators signals movement toward this scenario'
}

// Extract triggers from state - handles both per-state and axis-level triggers
function extractTriggersForState(state: ScenarioState, scenarioAxis: ScenarioAxis): { description: string; observability?: string; confidence?: number }[] {
  const stateName = state.label || ''
  const tone = determineTone(stateName, 0, 3)
  
  // Priority 1: Per-state triggers (from ScenarioAgent v2.2 schema)
  const stateExtras = state as any
  if (stateExtras.triggers && Array.isArray(stateExtras.triggers) && stateExtras.triggers.length > 0) {
    const triggers = stateExtras.triggers.map((t: any) => ({
      description: t.description || t.label || t.text || String(t),
      observability: t.observability,
      confidence: typeof t.confidence === 'number' ? t.confidence : undefined,
    }))
    
    // Check if all triggers are generic - if so, generate contextual ones
    const allGeneric = triggers.every((t: any) => isGenericTrigger(t.description))
    if (allGeneric) {
      return [{
        description: generateContextualTrigger(stateName, tone),
        observability: 'observable',
        confidence: 0.7,
      }]
    }
    
    // Filter out generic triggers, keep specific ones
    const specificTriggers = triggers.filter((t: any) => !isGenericTrigger(t.description))
    if (specificTriggers.length > 0) {
      return specificTriggers
    }
    
    // If only generic triggers, generate contextual one
    return [{
      description: generateContextualTrigger(stateName, tone),
      observability: 'observable',
      confidence: 0.7,
    }]
  }
  
  // Priority 2: Axis-level triggers mapped to this state
  const mappedTriggers = scenarioAxis.triggers
    ?.filter(t => t.maps_to_state_transitions?.some(tr => tr.to_state === state.state_id))
    .map(t => ({
      description: t.definition || t.label,
      observability: undefined,
      confidence: typeof t.confidence === 'string' ? 
        (t.confidence === 'high' ? 0.85 : t.confidence === 'medium' ? 0.65 : 0.45) : undefined
    }))
    .filter(t => !isGenericTrigger(t.description))
  
  if (mappedTriggers && mappedTriggers.length > 0) {
    return mappedTriggers
  }
  
  // Fallback: generate contextual trigger
  return [{
    description: generateContextualTrigger(stateName, tone),
    observability: 'observable',
    confidence: 0.7,
  }]
}

// Extract assumptions from state
function extractAssumptionsForState(state: ScenarioState, scenarioAxis: ScenarioAxis): { text: string; rationale?: string }[] {
  const stateExtras = state as any
  
  // Priority 1: Per-state assumptions
  if (stateExtras.assumptions && Array.isArray(stateExtras.assumptions) && stateExtras.assumptions.length > 0) {
    return stateExtras.assumptions.map((a: any) => ({
      text: a.text || a.assumption || String(a),
      rationale: a.rationale,
    }))
  }
  
  // Priority 2: Axis-level assumptions for this state
  const mappedAssumptions = scenarioAxis.assumptions
    ?.filter(a => !a.applies_to_state_id || a.applies_to_state_id === state.state_id)
    .map(a => ({ text: a.text, rationale: undefined }))
  
  return mappedAssumptions || []
}

// Generate journalistic narrative
/**
 * Generate Ezra Klein-style journalistic narrative for a scenario.
 * 
 * Ezra Klein's style characteristics:
 * - Clear, accessible explanation of complex ideas
 * - Uses conversational but authoritative tone
 * - Connects abstract concepts to concrete implications
 * - Acknowledges uncertainty while providing clarity
 * - Often frames in terms of "the story here is..." or "what matters is..."
 */
function generateJournalisticNarrative(scenario: DetailedScenario): string {
  const { description, subtitle, triggers, assumptions, metricImpacts, probability, tone } = scenario
  const prob = probability || 33
  
  let narrative = ''
  
  // Ezra Klein-style openings that frame the scenario with analytical clarity
  if (tone === 'upside') {
    if (prob >= 40) {
      narrative = `The story worth watching here is fundamentally optimistic: `
    } else {
      narrative = `There's a more hopeful path available, though it requires things to break right. In this scenario, `
    }
  } else if (tone === 'downside') {
    if (prob >= 35) {
      narrative = `The risk that deserves serious attention is this: `
    } else {
      narrative = `We should be honest about the downside risks, even if they're less likely. The concerning scenario involves `
    }
  } else if (tone === 'base') {
    narrative = `The baseline—and this is what most evidence points toward—suggests `
  } else {
    narrative = `Reality is rarely binary, and the most intellectually honest read here is more nuanced: `
  }
  
  // Add the core description
  const desc = description || subtitle || ''
  if (desc) {
    // Clean up the description for narrative flow
    let cleanDesc = desc.charAt(0).toLowerCase() + desc.slice(1)
    cleanDesc = cleanDesc.replace(/\.$/, '')
    narrative += cleanDesc + '. '
  }
  
  // Add key triggers in conversational style
  const validTriggers = (triggers || []).filter(t => t.description && !isGenericTrigger(t.description))
  if (validTriggers.length > 0) {
    const triggerText = validTriggers.slice(0, 2).map(t => t.description.toLowerCase().replace(/\.$/, '')).join(' and ')
    narrative += `The signals to watch: ${triggerText}. `
  }
  
  // Add expected outcomes
  const validImpacts = (metricImpacts || []).filter(m => m && !m.includes('undefined') && m.length > 5)
  if (validImpacts.length > 0) {
    const impactText = validImpacts.slice(0, 2).join('; ')
    narrative += `If this plays out, expect ${impactText.toLowerCase()}. `
  }
  
  return narrative.trim()
}

export function ScenarioAnalysisSection({ 
  scenarioAxis, 
  detailedScenarios,
  sources,
  topicName,
  className = '' 
}: ScenarioAnalysisSectionProps) {
  
  // Build scenarios from axis data or use provided
  const rawScenarios: DetailedScenario[] = detailedScenarios || scenarioAxis.states.map((state, idx) => {
    const scenarioName = state.label || (state as any).name || `Scenario ${idx + 1}`
    const stateExtras = state as any
    const total = scenarioAxis.states.length
    
    // Extract per-state triggers and assumptions
    const stateTriggers = extractTriggersForState(state, scenarioAxis)
    const stateAssumptions = extractAssumptionsForState(state, scenarioAxis)
    
    const prob = stateExtras.probability_band || stateExtras.probability
    const metricImpacts = stateExtras.metric_impacts || stateExtras.metricImpacts || []
    
    return {
      id: state.state_id || `scenario-${idx}`,
      name: scenarioName,
      subtitle: state.short_description || stateExtras.description,
      description: stateExtras.description || state.short_description,
      probabilityBand: prob,
      probability: parseProbability(prob).percentage,
      tone: determineTone(scenarioName, idx, total),
      triggers: stateTriggers,
      assumptions: stateAssumptions,
      metricImpacts,
    }
  })
  
  if (rawScenarios.length === 0) return null
  
  // CRITICAL: Detect and fix the 33%/33%/33% pattern (LLM failure mode)
  const scenarios = fixUniformProbabilities(rawScenarios)

  // Sort: upside, base, mixed, downside
  const toneOrder = { upside: 0, base: 1, mixed: 2, downside: 3 }
  const sortedScenarios = [...scenarios].sort((a, b) => toneOrder[a.tone] - toneOrder[b.tone])

  // Filter out ONLY placeholder triggers - keep signals with real labels
  const globalTriggers = (scenarioAxis.triggers || []).filter(trigger => {
    const label = (trigger.label || '').toLowerCase().trim()
    
    // Only filter out exact placeholder matches for the LABEL
    // These are known placeholder strings that have no real content
    const exactPlaceholders = [
      'global trigger',
      'monitor leading indicators',
      'monitor for changes',
      'key indicator to watch',
      'tbd',
      'n/a',
      'placeholder',
      ''
    ]
    
    // Skip ONLY if the label exactly matches a placeholder
    if (exactPlaceholders.includes(label)) {
      return false
    }
    
    // Skip if label contains "global trigger" (common LLM placeholder)
    if (label.includes('global trigger')) {
      return false
    }
    
    // Keep everything else - even short labels can be meaningful
    return true
  })
  
  // Extract clean topic name - prioritize explicit topic_name from axis
  const axisExtras = scenarioAxis as any
  const rawTopicName = topicName || axisExtras?.topic_name || scenarioAxis.label || 'this topic'
  
  // Clean up the topic name for display in Origin box
  const displayTopicName = cleanTopicForOrigin(rawTopicName)

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Scenario Narratives - Ezra Klein style journalistic summaries */}
      <div className="space-y-4">
        {sortedScenarios.map((scenario) => {
          const config = TONE_CONFIG[scenario.tone]
          const narrative = generateJournalisticNarrative(scenario)
          const { display: probDisplay } = parseProbability(scenario.probabilityBand || scenario.probability)
          
          return (
            <div 
              key={`narrative-${scenario.id}`}
              className="pl-4 border-l-3"
              style={{ borderLeftWidth: '3px', borderLeftColor: config.accent }}
            >
              <p className="text-[15px] text-gray-600 dark:text-gray-300 leading-relaxed">
                <span className="font-semibold text-gray-900 dark:text-white" style={{ color: config.accent }}>
                  {scenario.name}
                </span>
                <span className="text-gray-400 dark:text-gray-500 ml-1.5 text-sm">({probDisplay})</span>
                <span className="mx-2 text-gray-300 dark:text-gray-600">—</span>
                <span className="italic">{narrative}</span>
              </p>
            </div>
          )
        })}
      </div>

      {/* Scenario Flow Chart */}
      <div className="relative">
        {/* Origin */}
        <div className="flex justify-center mb-2">
          <div className="px-5 py-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-[10px] font-medium text-gray-400 uppercase tracking-widest text-center">Origin</div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white text-center max-w-md">
              {displayTopicName}
            </div>
          </div>
        </div>

        {/* Lines */}
        <svg className="w-full h-12" viewBox="0 0 1000 60" preserveAspectRatio="xMidYMid meet">
          <defs>
            {sortedScenarios.map((s, i) => (
              <linearGradient key={`grad-${i}`} id={`line-grad-${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#d1d5db" />
                <stop offset="100%" stopColor={TONE_CONFIG[s.tone].accent} />
              </linearGradient>
            ))}
          </defs>
          {sortedScenarios.map((scenario, idx) => {
            const total = sortedScenarios.length
            const startX = 500
            const endX = ((idx + 0.5) / total) * 1000
            
            return (
              <path
                key={scenario.id}
                d={`M ${startX} 0 L ${startX} 15 Q ${startX} 30, ${(startX + endX) / 2} 35 Q ${endX} 40, ${endX} 55`}
                stroke={`url(#line-grad-${idx})`}
                strokeWidth="1.5"
                fill="none"
              />
            )
          })}
        </svg>

        {/* Cards */}
        <div className={`grid gap-4 ${
          sortedScenarios.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 
          sortedScenarios.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {sortedScenarios.map((scenario) => {
            const config = TONE_CONFIG[scenario.tone]
            const { display: probDisplay, percentage } = parseProbability(scenario.probabilityBand || scenario.probability)
            const triggers = scenario.triggers || []
            const assumptions = scenario.assumptions || []
            const metricImpacts = (scenario.metricImpacts || []).filter(m => m && !m.includes('undefined'))
            
            return (
              <div
                key={scenario.id}
                className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Header */}
                <div 
                  className="px-4 py-3 border-b border-gray-100 dark:border-gray-800"
                  style={{ borderLeftWidth: '3px', borderLeftColor: config.accent }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-widest mb-1" style={{ color: config.accent }}>
                        {config.label}
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                        {scenario.name}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{probDisplay}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider">likelihood</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: config.accent }} />
                  </div>
                </div>

                {/* Sections with icons */}
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {/* What */}
                  {scenario.subtitle && (
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        {SECTION_ICONS.what}
                        <span className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider">What</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {scenario.subtitle}
                      </p>
                    </div>
                  )}

                  {/* Triggers */}
                  {triggers.length > 0 && (
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        {SECTION_ICONS.triggers}
                        <span className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider">Triggers</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {triggers.map(t => t.description).join('; ')}
                      </p>
                    </div>
                  )}

                  {/* Impact */}
                  {metricImpacts.length > 0 && (
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        {SECTION_ICONS.impact}
                        <span className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider">Impact</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {metricImpacts.join('; ')}
                      </p>
                    </div>
                  )}

                  {/* Assumptions */}
                  {assumptions.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
                      <div className="flex items-center gap-2 mb-2">
                        {SECTION_ICONS.assumptions}
                        <span className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider">Assumptions</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {assumptions.map(a => a.text).join('; ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Global Triggers / Key Signals */}
      {globalTriggers.length > 0 && (
        <KeySignalsCarousel triggers={globalTriggers} />
      )}

      {/* Source Links */}
      {sources && sources.length > 0 && (
        <div className="pt-4 flex flex-wrap gap-3">
          {sources.slice(0, 10).map((source, idx) => (
            <a
              key={source.id || idx}
              href={source.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
              <span className="font-medium">[{idx + 1}]</span>
            </a>
          ))}
          {sources.length > 10 && (
            <span className="text-sm text-gray-400">+{sources.length - 10} more</span>
          )}
        </div>
      )}
    </div>
  )
}

export default ScenarioAnalysisSection
